import React from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import {
  CheckCircle2,
  FilePlus2,
  Link2,
  Loader2,
  RefreshCw,
  Send,
  Sparkles,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  WhatsAppLifecycleEvent,
  WhatsAppTemplate,
  WhatsAppTemplateBinding,
  WhatsAppTemplateBlueprint,
  WhatsAppTemplateStatus,
} from "@/types/whatsapp";
import {
  useBindWhatsAppRecommendedTemplate,
  useBootstrapWhatsAppTemplates,
  useCreateWhatsAppTemplateDraft,
  useSubmitWhatsAppTemplateDraft,
  useWhatsAppTemplateBindings,
  useWhatsAppTemplateBlueprints,
  useWhatsAppTemplates,
} from "../hooks/whatsapp-provisioning.hooks";

interface WhatsappTemplateOperationsPanelProps {
  hotelId: string;
}

type TemplateOperationStatus =
  | "MISSING"
  | "DRAFT"
  | "PENDING"
  | "APPROVED"
  | "BOUND"
  | "REJECTED"
  | "DISABLED"
  | "DEPRECATED";

interface TemplateOperationRow {
  blueprint: WhatsAppTemplateBlueprint;
  template: WhatsAppTemplate | null;
  binding: WhatsAppTemplateBinding | null;
  status: TemplateOperationStatus;
}

export const WhatsappTemplateOperationsPanel: React.FC<
  WhatsappTemplateOperationsPanelProps
> = ({ hotelId }) => {
  const { t } = useTranslation();
  const blueprintsQuery = useWhatsAppTemplateBlueprints(hotelId, true);
  const templatesQuery = useWhatsAppTemplates(hotelId, true);
  const bindingsQuery = useWhatsAppTemplateBindings(hotelId, true);
  const createDraft = useCreateWhatsAppTemplateDraft(hotelId);
  const submitDraft = useSubmitWhatsAppTemplateDraft(hotelId);
  const bindTemplate = useBindWhatsAppRecommendedTemplate(hotelId);
  const bootstrapTemplates = useBootstrapWhatsAppTemplates(hotelId);
  const isBusy =
    blueprintsQuery.isFetching ||
    templatesQuery.isFetching ||
    bindingsQuery.isFetching ||
    createDraft.isPending ||
    submitDraft.isPending ||
    bindTemplate.isPending ||
    bootstrapTemplates.isPending;

  const rows = React.useMemo(
    () =>
      buildTemplateRows(
        blueprintsQuery.data ?? [],
        templatesQuery.data ?? [],
        bindingsQuery.data ?? [],
      ),
    [blueprintsQuery.data, templatesQuery.data, bindingsQuery.data],
  );

  const handleRefresh = () => {
    blueprintsQuery.refetch();
    templatesQuery.refetch();
    bindingsQuery.refetch();
  };

  const handleBootstrap = () => {
    bootstrapTemplates.mutate(undefined, {
      onSuccess: (result) => {
        const toastParams = {
          created: result.createdDrafts,
          submitted: result.submitted,
          bound: result.bound,
          pending: result.pending,
          failed: result.failed,
        };
        if (result.failed > 0 || result.syncError) {
          toast.warning(
            t(
              "communicationChannels.whatsapp.templateOps.toast.bootstrapPartial",
              toastParams,
            ),
          );
          return;
        }
        toast.success(
          t(
            "communicationChannels.whatsapp.templateOps.toast.bootstrapSuccess",
            toastParams,
          ),
        );
      },
      onError: () =>
        toast.error(
          t("communicationChannels.whatsapp.templateOps.toast.bootstrapError"),
        ),
    });
  };

  const handleCreate = (event: WhatsAppLifecycleEvent) => {
    createDraft.mutate(event, {
      onSuccess: () =>
        toast.success(
          t("communicationChannels.whatsapp.templateOps.toast.draftCreated"),
        ),
      onError: () =>
        toast.error(
          t("communicationChannels.whatsapp.templateOps.toast.draftError"),
        ),
    });
  };

  const handleSubmit = (templateId: string) => {
    submitDraft.mutate(templateId, {
      onSuccess: () =>
        toast.success(
          t("communicationChannels.whatsapp.templateOps.toast.submitted"),
        ),
      onError: () =>
        toast.error(
          t("communicationChannels.whatsapp.templateOps.toast.submitError"),
        ),
    });
  };

  const handleBind = (templateId: string) => {
    bindTemplate.mutate(templateId, {
      onSuccess: () =>
        toast.success(
          t("communicationChannels.whatsapp.templateOps.toast.bound"),
        ),
      onError: () =>
        toast.error(
          t("communicationChannels.whatsapp.templateOps.toast.bindError"),
        ),
    });
  };

  return (
    <section className="flex flex-col gap-4 border-t pt-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex min-w-0 flex-col gap-1">
          <h4 className="text-sm font-medium text-foreground">
            {t("communicationChannels.whatsapp.templateOps.title")}
          </h4>
          <p className="text-xs text-muted-foreground">
            {t("communicationChannels.whatsapp.templateOps.description")}
          </p>
        </div>

        <div className="flex items-center gap-2">
          <Button
            type="button"
            variant="new_default"
            size="sm"
            disabled={isBusy}
            onClick={handleBootstrap}
          >
            {bootstrapTemplates.isPending ? (
              <Loader2 className="size-4 animate-spin" />
            ) : (
              <Sparkles className="size-4" />
            )}
            {bootstrapTemplates.isPending
              ? t(
                  "communicationChannels.whatsapp.templateOps.actions.bootstrapping",
                )
              : t(
                  "communicationChannels.whatsapp.templateOps.actions.bootstrap",
                )}
          </Button>
          <Button
            type="button"
            variant="new_outline"
            size="sm"
            disabled={isBusy}
            onClick={handleRefresh}
          >
            <RefreshCw
              className={
                blueprintsQuery.isFetching ||
                templatesQuery.isFetching ||
                bindingsQuery.isFetching
                  ? "size-4 animate-spin"
                  : "size-4"
              }
            />
            {t("communicationChannels.whatsapp.templateOps.actions.refresh")}
          </Button>
        </div>
      </div>

      {renderBody({
        isLoading:
          blueprintsQuery.isLoading ||
          templatesQuery.isLoading ||
          bindingsQuery.isLoading,
        isError:
          blueprintsQuery.isError ||
          templatesQuery.isError ||
          bindingsQuery.isError,
        rows,
        isBusy,
        t,
        onCreate: handleCreate,
        onSubmit: handleSubmit,
        onBind: handleBind,
      })}
    </section>
  );
};

const buildTemplateRows = (
  blueprints: WhatsAppTemplateBlueprint[],
  templates: WhatsAppTemplate[],
  bindings: WhatsAppTemplateBinding[],
): TemplateOperationRow[] =>
  blueprints.map((blueprint) => {
    const template =
      templates.find(
        (candidate) =>
          candidate.language === blueprint.language &&
          candidate.metadata?.lifecycleEvent === blueprint.event &&
          candidate.metadata?.blueprintName === blueprint.name,
      ) ??
      templates.find(
        (candidate) =>
          candidate.name === blueprint.name &&
          candidate.language === blueprint.language,
      ) ??
      null;
    const binding =
      bindings.find(
        (candidate) =>
          candidate.enabled &&
          candidate.event === blueprint.event &&
          candidate.language === blueprint.language &&
          (!template || candidate.templateId === template.id),
      ) ?? null;

    return {
      blueprint,
      template,
      binding,
      status: resolveOperationStatus(template, binding),
    };
  });

const resolveOperationStatus = (
  template: WhatsAppTemplate | null,
  binding: WhatsAppTemplateBinding | null,
): TemplateOperationStatus => {
  if (template && binding?.templateId === template.id) return "BOUND";
  if (!template) return "MISSING";
  if (template.status === "PAUSED") return "DISABLED";
  return template.status as TemplateOperationStatus;
};

interface RenderBodyInput {
  isLoading: boolean;
  isError: boolean;
  rows: TemplateOperationRow[];
  isBusy: boolean;
  t: (key: string, options?: Record<string, unknown>) => string;
  onCreate: (event: WhatsAppLifecycleEvent) => void;
  onSubmit: (templateId: string) => void;
  onBind: (templateId: string) => void;
}

const renderBody = ({
  isLoading,
  isError,
  rows,
  isBusy,
  t,
  onCreate,
  onSubmit,
  onBind,
}: RenderBodyInput): React.ReactNode => {
  if (isLoading) {
    return (
      <p className="text-xs text-muted-foreground">
        {t("communicationChannels.whatsapp.templateOps.loading")}
      </p>
    );
  }

  if (isError) {
    return (
      <p className="text-xs text-destructive">
        {t("communicationChannels.whatsapp.templateOps.loadError")}
      </p>
    );
  }

  return (
    <div className="grid gap-2">
      {rows.map((row) => (
        <TemplateOperationRow
          key={`${row.blueprint.event}-${row.blueprint.language}`}
          row={row}
          isBusy={isBusy}
          t={t}
          onCreate={onCreate}
          onSubmit={onSubmit}
          onBind={onBind}
        />
      ))}
    </div>
  );
};

interface TemplateOperationRowProps {
  row: TemplateOperationRow;
  isBusy: boolean;
  t: (key: string, options?: Record<string, unknown>) => string;
  onCreate: (event: WhatsAppLifecycleEvent) => void;
  onSubmit: (templateId: string) => void;
  onBind: (templateId: string) => void;
}

const TemplateOperationRow: React.FC<TemplateOperationRowProps> = ({
  row,
  isBusy,
  t,
  onCreate,
  onSubmit,
  onBind,
}) => {
  const { blueprint, template, status } = row;
  return (
    <div className="grid gap-3 rounded-md border bg-background p-3 md:grid-cols-[minmax(0,1fr)_auto]">
      <div className="flex min-w-0 flex-col gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="text-sm font-medium text-foreground">
            {t(
              `communicationChannels.whatsapp.templateOps.event.${blueprint.event}`,
            )}
          </span>
          <Badge variant={statusVariant(status)}>
            {t(`communicationChannels.whatsapp.templateOps.status.${status}`)}
          </Badge>
          {template?.status && template.status !== status && (
            <Badge variant={statusVariant(template.status)}>
              {template.status}
            </Badge>
          )}
        </div>

        <span
          className="truncate font-mono text-xs text-muted-foreground"
          title={template?.name ?? blueprint.name}
        >
          {template?.name ?? blueprint.name}
        </span>
        {template?.statusReason &&
          template.statusReason.trim().toUpperCase() !== "NONE" && (
            <p
              className="line-clamp-2 text-xs text-destructive"
              title={template.statusReason}
            >
              {template.statusReason}
            </p>
          )}
      </div>

      <TemplateAction
        row={row}
        isBusy={isBusy}
        t={t}
        onCreate={onCreate}
        onSubmit={onSubmit}
        onBind={onBind}
      />
    </div>
  );
};

const TemplateAction: React.FC<TemplateOperationRowProps> = ({
  row,
  isBusy,
  t,
  onCreate,
  onSubmit,
  onBind,
}) => {
  if (!row.template) {
    return (
      <Button
        type="button"
        variant="new_outline"
        size="sm"
        disabled={isBusy}
        className="self-start"
        onClick={() => onCreate(row.blueprint.event)}
      >
        <FilePlus2 className="size-4" />
        {t("communicationChannels.whatsapp.templateOps.actions.create")}
      </Button>
    );
  }

  if (row.template.status === "DRAFT") {
    return (
      <Button
        type="button"
        variant="new_outline"
        size="sm"
        disabled={isBusy}
        className="self-start"
        onClick={() => onSubmit(row.template!.id)}
      >
        {isBusy ? (
          <Loader2 className="size-4 animate-spin" />
        ) : (
          <Send className="size-4" />
        )}
        {t("communicationChannels.whatsapp.templateOps.actions.submit")}
      </Button>
    );
  }

  if (row.template.status === "APPROVED" && row.status !== "BOUND") {
    return (
      <Button
        type="button"
        variant="new_default"
        size="sm"
        disabled={isBusy}
        className="self-start"
        onClick={() => onBind(row.template!.id)}
      >
        {isBusy ? (
          <Loader2 className="size-4 animate-spin" />
        ) : (
          <Link2 className="size-4" />
        )}
        {t("communicationChannels.whatsapp.templateOps.actions.bind")}
      </Button>
    );
  }

  if (row.status === "BOUND") {
    return (
      <div className="flex items-center gap-2 self-start text-xs font-medium text-emerald-600">
        <CheckCircle2 className="size-4" />
        {t("communicationChannels.whatsapp.templateOps.actions.ready")}
      </div>
    );
  }

  return null;
};

const statusVariant = (
  status: TemplateOperationStatus | WhatsAppTemplateStatus,
): React.ComponentProps<typeof Badge>["variant"] => {
  if (status === "BOUND" || status === "APPROVED") return "success";
  if (status === "REJECTED" || status === "DISABLED") {
    return "new_destructive";
  }
  if (status === "MISSING" || status === "DEPRECATED") return "new_secondary";
  return "new_default";
};
